import {
  ANSWER_STREAM,
  type AnswerEvent,
  type AnswerFeedback,
  type AnswerMeta,
  type AnswerResponse,
} from '../../shared/answers.ts';
import {
  ACCOUNT_HEADER,
  type AdminAuditResponse,
  type AdminFeaturesResponse,
  type AdminPreviewRequest,
  type AdminPreviewResponse,
  type AdminRunRequest,
  type AdminRunResponse,
  type AdminRunStartResponse,
  type AdminRunsResponse,
  type AdminUsageResponse,
  type CompleteResponse,
  type ConfigResponse,
  type CreateLiveSessionResponse,
  type MemoryResponse,
  type MeResponse,
  type ToolResponse,
} from '../../shared/api.ts';
import type { Progress } from '../../shared/assessment.ts';
import type { SessionEvent } from '../../shared/events.ts';
import type { JourneyStage } from '../../shared/memory.ts';
import type { FlagLevel } from '../../shared/plan/flags.ts';
import type { SpeechScores } from '../../shared/scoring.ts';
import type { InterviewForm, SetupResponse } from '../../shared/setup.ts';
import { accountHeaders, mockHeaders } from '../shell/mocks.ts';

export type AdminFixtures = { sessions: string[]; audio: string[] };
type Account = 'test' | 'own';

/** A refusal the server answers with 200 {error} (see app.ts) becomes an error the call screen can show. */
function orThrow<T extends object>(body: T | { error: string }): T {
  if ('error' in body && typeof body.error === 'string') throw new Error(body.error);
  return body as T;
}

async function call<T>(
  path: string,
  init?: Omit<RequestInit, 'headers'> & { json?: unknown; headers?: Record<string, string> },
): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: { ...(init?.json !== undefined ? { 'content-type': 'application/json' } : {}), ...init?.headers },
    body: init?.json !== undefined ? JSON.stringify(init.json) : init?.body,
  });
  const body = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw Object.assign(new Error(body.error ?? `${path} failed (${res.status})`), { status: res.status });
  return body;
}

export const api = {
  config: () => call<ConfigResponse>('/api/config'),
  me: () => call<MeResponse>('/api/me'),
  signInGoogle: (credential: string) => call<MeResponse>('/api/auth/google', { method: 'POST', json: { credential } }),
  signInDev: (email: string) => call<MeResponse>('/api/auth/dev', { method: 'POST', json: { email } }),
  signOut: () => call<MeResponse>('/api/auth/logout', { method: 'POST', json: {} }),
  createLiveSession: (transport: 'gemini' | 'scripted') =>
    call<CreateLiveSessionResponse | { error: string }>('/api/live/sessions', {
      method: 'POST',
      json: { transport },
      headers: accountHeaders(),
    }).then(orThrow),
  renewLiveToken: (sessionId: string) =>
    call<CreateLiveSessionResponse | { error: string }>(`/api/live/sessions/${sessionId}/token`, {
      method: 'POST',
      json: {},
    }).then(orThrow),
  appendEvents: (sessionId: string, events: SessionEvent[]) =>
    call<{ ok: true }>(`/api/sessions/${sessionId}/events`, { method: 'POST', json: { events }, keepalive: true }),
  runTool: (name: string, params: unknown, sessionId: string) =>
    call<ToolResponse>(`/api/tools/${name}`, { method: 'POST', json: { params, sessionId } }),
  /** Posts a spoken answer. `onFeedback` fires as soon as the review lands; the promise settles once scores are in. */
  submitAnswer: async (
    meta: AnswerMeta,
    wav: Uint8Array,
    onFeedback?: (feedback: AnswerFeedback) => void,
  ): Promise<AnswerResponse | { error: string }> => {
    const form = new FormData();
    form.append('audio', new Blob([wav as Uint8Array<ArrayBuffer>], { type: 'audio/wav' }), 'answer.wav');
    form.append('meta', JSON.stringify(meta));
    const res = await fetch('/api/answers', {
      method: 'POST',
      body: form,
      credentials: 'same-origin',
      headers: { accept: ANSWER_STREAM, ...mockHeaders() },
    });
    if (!res.ok || !res.body || !res.headers.get('content-type')?.includes(ANSWER_STREAM)) {
      const body = (await res.json().catch(() => ({}))) as AnswerResponse & { error?: string };
      if (!res.ok)
        throw Object.assign(new Error(body.error ?? `/api/answers failed (${res.status})`), { status: res.status });
      return body;
    }
    let feedback: AnswerFeedback | null = null;
    let scores: SpeechScores | undefined;
    let progress: Progress | undefined;
    try {
      for await (const event of ndjson<AnswerEvent>(res.body)) {
        if ('error' in event) return { error: event.error };
        if ('feedback' in event) {
          feedback = event.feedback;
          onFeedback?.(event.feedback);
        }
        if ('scores' in event) scores = event.scores;
        if ('progress' in event) progress = event.progress;
      }
    } catch (err) {
      // The connection dropped after the review arrived (a phone changing networks). The review was shown, and the
      // server still records the answer once scoring settles, so it stands; the card says the scores didn't come
      // through. Before the review, it's a failed answer.
      if (!feedback) throw err;
      console.warn('[fuse] answer stream dropped after the review', err);
      scores = { status: 'not_scored', reason: 'unavailable' };
    }
    if (!feedback) return { error: 'the answer stream ended early' };
    return { feedback, ...(scores ? { scores } : {}), ...(progress ? { progress } : {}) };
  },
  memory: () => call<MemoryResponse>('/api/memory'),
  editMemory: (field: string, value: string) =>
    call<MemoryResponse>('/api/memory', { method: 'PATCH', json: { field, value } }),
  forgetFact: (text: string) => call<MemoryResponse>('/api/memory/facts/forget', { method: 'POST', json: { text } }),
  completeSession: (sessionId: string) =>
    call<CompleteResponse>(`/api/sessions/${sessionId}/complete`, { method: 'POST', json: {} }),
  uploadResume: (file: Blob) => {
    const form = new FormData();
    form.append('file', file, 'resume.pdf');
    return call<SetupResponse>('/api/setup/resume', { method: 'POST', body: form });
  },
  removeResume: () => call<MemoryResponse>('/api/setup/resume', { method: 'DELETE' }),
  removeJd: () => call<MemoryResponse>('/api/setup/jd', { method: 'DELETE' }),
  saveInterview: (form: InterviewForm) => call<MemoryResponse>('/api/setup/interview', { method: 'PUT', json: form }),
  finishSetup: () => call<MemoryResponse>('/api/setup/done', { method: 'POST', json: {} }),
  // Admins only (the server answers 404 to everyone else).
  adminSandbox: (on: boolean) =>
    call<MeResponse | { error: string }>('/api/admin/sandbox', { method: 'POST', json: { on } }).then(orThrow),
  // `account`: the one this tab shows; the server refuses the write if the browser has switched since.
  adminSetStage: (stage: JourneyStage, account: Account) =>
    call<MemoryResponse>('/api/admin/memory', {
      method: 'PUT',
      json: { stage },
      headers: { [ACCOUNT_HEADER]: account },
    }),
  adminSetMemory: (memory: unknown, basedOn: number, account: Account) =>
    call<MemoryResponse>('/api/admin/memory', {
      method: 'PUT',
      json: { memory, basedOn },
      headers: { [ACCOUNT_HEADER]: account },
    }),
  adminFixtures: () => call<AdminFixtures>('/api/admin/fixtures'),
  // The admin page (ADR-0014). `basedOn`: the version the page showed; a change made since is refused (409).
  adminFeatures: () => call<AdminFeaturesResponse>('/api/admin/features'),
  // `expect`: the level the page said users would get, so a default changed by a deploy since is refused.
  adminSetFeature: (name: string, level: FlagLevel | 'default', reason: string, basedOn: number, expect: FlagLevel) =>
    call<AdminFeaturesResponse>(`/api/admin/features/${encodeURIComponent(name)}`, {
      method: 'PUT',
      json: { level, reason, basedOn, expect },
    }),
  adminAudit: () => call<AdminAuditResponse>('/api/admin/audit'),
  adminUsage: (days: number) => call<AdminUsageResponse>(`/api/admin/usage?days=${days}`),
  adminPreview: (request: AdminPreviewRequest) =>
    call<AdminPreviewResponse>('/api/admin/preview', { method: 'POST', json: request }),
  // Test runs (ADR-0015). A refusal (busy, a cap) comes back as `{error}` with 200.
  adminStartRun: (request: AdminRunRequest) =>
    call<AdminRunStartResponse>('/api/admin/runs', { method: 'POST', json: request }),
  adminRuns: () => call<AdminRunsResponse>('/api/admin/runs'),
  adminRun: (id: string) => call<AdminRunResponse>(`/api/admin/runs/${encodeURIComponent(id)}`),
};
export type Api = typeof api;

/** Parses a stream of newline-separated JSON objects as they arrive. */
export async function* ndjson<T>(body: ReadableStream<Uint8Array>): AsyncGenerator<T> {
  const reader = body.getReader();
  const text = new TextDecoder();
  let buf = '';
  for (let r = await reader.read(); !r.done; r = await reader.read()) {
    buf += text.decode(r.value, { stream: true });
    let nl = buf.indexOf('\n');
    while (nl >= 0) {
      const line = buf.slice(0, nl).trim();
      buf = buf.slice(nl + 1);
      if (line) yield JSON.parse(line) as T;
      nl = buf.indexOf('\n');
    }
  }
  if (buf.trim()) yield JSON.parse(buf) as T;
}
